import { appPath } from '../lib/basePath';
/**
 * 0.36.171 - Shared formatting utilities.
 *
 * Extracted from inline duplications across Profile.jsx,
 * AdminEmailTemplates.jsx, AdminUserDetail.jsx, AdminGeoIP.jsx,
 * PageContentRenderer.jsx, and AdminFormPreview.jsx. Identical
 * implementations - no behavior change.
 */

/**
 * Format an ISO-8601 timestamp string using the browser's locale.
 * Falls back to the input string if the date can't be parsed.
 *
 * 0.36.227 — Now reads system time format (date_format, time_format)
 * from module state set by App.jsx at mount, and the current user's
 * timezone (IANA) from module state set by AuthContext on login /
 * profile refresh. Both fall back to sensible defaults when state
 * isn't initialized yet (anonymous pages, first paint before mount).
 *
 * For absolute timestamps in the UI, prefer `formatDateTime(iso)` —
 * `formatDate` is preserved for backward compatibility and now
 * produces date+time output (matching the original `toLocaleString`
 * behavior), just routed through the configured format.
 *
 * @param {string|null|undefined} iso
 * @returns {string} formatted date+time, or "—" if input is falsy
 */
export function formatDate(iso) {
  return formatDateTime(iso);
}


// =====================================================================
// 0.36.227 — Module-level state for timezone + format
// =====================================================================
//
// Set from App.jsx (system formats, fetched from /api/public/settings)
// and from AuthContext (user timezone, from /api/me/profile).
//
// The module-level singleton means every formatter call is a plain
// function with one argument — no Context plumbing for every consumer.
// Acceptable because:
//   - One user session per browser tab
//   - System format changes are admin-driven and rare
//   - Components that need to re-render on format change can subscribe
//     via a future hook; for now they re-render naturally when their
//     parent re-fetches data
// =====================================================================

// 0.44.69 - Two pairs: the admin panel's and the public site's (Settings
// > Time & date). Which applies is decided per call by where the page
// is (_surface below), so every formatter keeps its one argument.
let _adminDateFormat = 'dd.mm.yyyy';
let _adminTimeFormat = 'HH:mm';
let _publicDateFormat = 'dd.mm.yyyy';
let _publicTimeFormat = 'HH:mm';
// 'public' while an admin Preview shows a public page (set by the
// preview), else null: then /admin... pages are 'admin'.
let _surfaceOverride = null;
function _surface() {
  if (_surfaceOverride) return _surfaceOverride;
  try {
    return typeof window !== 'undefined' && window.location
      && appPath().startsWith('/admin') ? 'admin' : 'public';
  } catch { return 'public'; }
}
function _df() { return _surface() === 'admin' ? _adminDateFormat : _publicDateFormat; }
function _tf() { return _surface() === 'admin' ? _adminTimeFormat : _publicTimeFormat; }
export function setFormatSurface(surface) {
  const next = surface === 'admin' || surface === 'public' ? surface : null;
  if (next !== _surfaceOverride) { _surfaceOverride = next; _notifyFormatChange(); }
}

// 0.44.69 - Month names for the MMM / MMMM formats, per language, from
// the built-in library Months and days (registered by libraryCache when
// it loads). The admin panel is English; until a language's names have
// loaded, English is used and a load is asked for.
const _EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];
const _monthNames = { en: { long: _EN_MONTHS, short: _EN_MONTHS.map(m => m.slice(0, 3)) } };
let _monthLoader = null;
const _monthAsked = new Set();
/* 0.44.71 - dateLong / dateShort: the month as written after a day
 * number (Month in a date: "11 հունվարի", "25 июня"); empty entries fall
 * back to the plain names. */
export function registerMonthNames(lang, long, short, dateLong, dateShort) {
  if (!lang || !Array.isArray(long) || long.length !== 12) return;
  const sh = Array.isArray(short) && short.length === 12 ? short : long;
  const fill = (arr, base) => base.map((b, i) => (Array.isArray(arr) && arr[i]) || b);
  const next = { long, short: sh, dateLong: fill(dateLong, long), dateShort: fill(dateShort, sh) };
  const prev = _monthNames[lang];
  const key = (x) => [x.long, x.short, x.dateLong || [], x.dateShort || []].map(a => a.join('|')).join('#');
  if (prev && key(prev) === key(next)) return;
  _monthNames[lang] = next;
  _notifyFormatChange();
}
export function setMonthNamesLoader(fn) { _monthLoader = typeof fn === 'function' ? fn : null; }
function _pageLang() {
  if (_surface() === 'admin') return 'en';
  try { return (document.documentElement.getAttribute('lang') || 'en').slice(0, 8); } catch { return 'en'; }
}
/* 0.44.70 - For formatdate / parsedate in rules: the page language's
 * names (English until loaded). */
export function monthNameFor(month, short, inDate = false) { return _monthName(month, short, inDate); }
export function monthNamesNow() {
  const set = _monthNames[_pageLang()] || _monthNames.en;
  return { long: set.long.slice(), short: set.short.slice(),
           dateLong: (set.dateLong || set.long).slice(), dateShort: (set.dateShort || set.short).slice() };
}
function _monthName(month, short, inDate = false) {
  const lang = _pageLang();
  const set = _monthNames[lang];
  if (!set && _monthLoader && !_monthAsked.has(lang)) {
    _monthAsked.add(lang);
    try { _monthLoader(lang); } catch { /* English meanwhile */ }
  }
  const use = set || _monthNames.en;
  const names = (inDate ? use[short ? 'dateShort' : 'dateLong'] : null) || use[short ? 'short' : 'long'];
  return names[Number(month) - 1] || String(month);
}
// 0.36.530 — Platform default IANA timezone (set from
// /api/public/settings at App mount). Used as the fallback when a
// user has no `timezone` set on their profile. Previously fell
// back to 'UTC', which showed timestamps N hours behind local
// wall-clock for admins who never set their profile timezone —
// most visibly in the audit log ("4 hours back" report from
// admin, since the Armenia deployment defaults to Asia/Yerevan =
// UTC+4).
let _systemDefaultTimezone = 'UTC';
let _currentUserTimezone = 'UTC';

// 0.36.531 — Subscription mechanism so React components re-render
// when date/time settings change. Ship 0.36.530 correctly updated
// the module state after publicSettings + AuthContext resolved,
// but components that had already rendered (with the initial 'UTC'
// values) never re-rendered — module-state mutation doesn't
// trigger React. Result: audit log kept showing 4-hours-back
// timestamps even after the fallback loaded, until admin
// navigated away and back.
//
// Now: setSystemTimeFormat / setCurrentUserTimezone bump a
// version counter and notify subscribers. Components use the
// `useFormatVersion()` hook to subscribe — any dependency
// referencing the version invalidates when settings change, so
// the very next render reads the fresh module state.
let _formatVersion = 0;
const _formatSubscribers = new Set();

function _notifyFormatChange() {
  _formatVersion += 1;
  for (const cb of _formatSubscribers) {
    try { cb(); } catch { /* subscriber errors don't cascade */ }
  }
}

export function subscribeFormatChange(cb) {
  _formatSubscribers.add(cb);
  return () => _formatSubscribers.delete(cb);
}
export function getFormatVersion() {
  return _formatVersion;
}

/**
 * 0.36.547 — Getters for the current system format. Used by the
 * library detail page to render date-column input placeholders that
 * match the platform's date/time format. Reactive via
 * `useFormatVersion()` — placeholders re-render when admin changes
 * the platform setting.
 */
export function getSystemDateFormat() {
  return _df();
}
export function getSystemTimeFormat() {
  return _tf();
}

/**
 * 0.36.547 — Render a placeholder string that mirrors the system
 * date format using standard `D`/`M`/`Y` (or 24h/AM-PM) tokens.
 */
export function datePlaceholder() {
  switch (_df()) {
    case 'dd.mm.yyyy': return 'DD.MM.YYYY';
    case 'dd/mm/yyyy': return 'DD/MM/YYYY';
    case 'mm/dd/yyyy': return 'MM/DD/YYYY';
    case 'dd MMM yyyy': return 'DD Mon YYYY';
    case 'dd MMMM yyyy': return 'DD Month YYYY';
    case 'MMM dd, yyyy': return 'Mon DD, YYYY';
    case 'MMMM dd, yyyy': return 'Month DD, YYYY';
    case 'yyyy-mm-dd':
    default: return 'YYYY-MM-DD';
  }
}
export function timePlaceholder() {
  return _tf() === 'hh:mm a' ? 'HH:MM AM/PM' : 'HH:MM';
}
export function datetimePlaceholder() {
  return `${datePlaceholder()} ${timePlaceholder()}`;
}


/**
 * 0.36.227 — Set the system-wide date/time format. Called once at app
 * mount after /api/public/settings resolves. Subsequent admin changes
 * (Time & date settings page) take effect on next page load — no live
 * push.
 *
 * @param {object} opts
 * @param {string} [opts.dateFormat]  One of 'dd.mm.yyyy', 'dd/mm/yyyy',
 *                                    'mm/dd/yyyy', 'yyyy-mm-dd'
 * @param {string} [opts.timeFormat]  'HH:mm' (24h) or 'hh:mm a' (12h)
 */
export function setSystemTimeFormat({ dateFormat, timeFormat, defaultTimezone,
  publicDateFormat, publicTimeFormat } = {}) {
  let changed = false;
  if (dateFormat && dateFormat !== _adminDateFormat) {
    _adminDateFormat = dateFormat;
    changed = true;
  }
  if (timeFormat && timeFormat !== _adminTimeFormat) {
    _adminTimeFormat = timeFormat;
    changed = true;
  }
  // 0.44.69 - The public site's pair (falls back to the admin panel's).
  const pd = publicDateFormat || dateFormat;
  const pt = publicTimeFormat || timeFormat;
  if (pd && pd !== _publicDateFormat) { _publicDateFormat = pd; changed = true; }
  if (pt && pt !== _publicTimeFormat) { _publicTimeFormat = pt; changed = true; }
  // 0.36.530 / 0.36.531 — Also mirrors the platform default IANA
  // timezone so formatDateTime has a sensible fallback for users
  // who haven't set their profile timezone.
  if (defaultTimezone && defaultTimezone !== _systemDefaultTimezone) {
    _systemDefaultTimezone = defaultTimezone;
    // If the current user's timezone hasn't been set yet (or is
    // still the initial 'UTC' default from module load) apply the
    // new system default retroactively so timestamps rendered
    // BEFORE login use the right zone. Set at login-time takes
    // over from here.
    if (_currentUserTimezone === 'UTC') {
      _currentUserTimezone = defaultTimezone;
    }
    changed = true;
  }
  if (changed) _notifyFormatChange();
}


/**
 * 0.36.227 — Set the current user's IANA timezone. Called from
 * AuthContext after the profile is loaded (login or refresh). Pass
 * null on logout to reset to UTC.
 *
 * @param {string|null} iana  e.g. 'Asia/Yerevan'
 */
export function setCurrentUserTimezone(iana) {
  // 0.36.530 — Fall back to the platform default IANA timezone (set
  // via setSystemTimeFormat from public /settings) rather than
  // hardcoded 'UTC'. Admins who never set a timezone on their
  // profile now see timestamps in the deployment's default zone
  // (Asia/Yerevan for the Armenia deployment). Explicit null still
  // triggers the fallback so logout doesn't leave the last user's
  // timezone leaking into anonymous pages.
  const resolved = iana || _systemDefaultTimezone || 'UTC';
  if (resolved !== _currentUserTimezone) {
    _currentUserTimezone = resolved;
    _notifyFormatChange();
  }
}


/**
 * 0.36.227 — Format an ISO-8601 UTC timestamp in the current user's
 * timezone using the configured system date and time format.
 *
 * DST handling is delegated to `Intl.DateTimeFormat` — when the target
 * timezone is in a DST window at the moment of `iso`, the offset shifts
 * automatically.
 *
 * @param {string|null|undefined} iso
 * @returns {string} e.g. '17.06.2026 23:45', or '—' if input is falsy
 */
// 0.36.532 — Parse ISO strings that lack timezone information as UTC
// rather than local time. Backend stores naive UTC datetimes
// (datetime.utcnow()) which Pydantic serializes without a Z suffix
// or +offset. Left to itself, `new Date("2026-07-04T20:31:16")` in
// a non-UTC browser interprets the string as LOCAL wall-clock time,
// which shifts the wall value by the browser's offset before any
// downstream timezone formatting can correct it. Result: audit-log
// timestamps rendered 4 hours off in Asia/Yerevan (+4), 5 off in
// Yekaterinburg, etc.
// This helper appends 'Z' when the string has no timezone marker,
// forcing UTC interpretation. Strings that already carry Z or an
// explicit ±hh:mm offset pass through unchanged.
// 0.44.66 - Exported for pages that compute with a server timestamp
// (days until an invite expires, say).
export function parseTimestamp(iso) { return _parseTimestamp(iso); }
/* 0.49.07 - The time zone dates are shown in (the person's own, else the
   platform's), for today() and now() in formulas. */
export function currentTimezone() { return _currentUserTimezone || _systemDefaultTimezone || 'UTC'; }

function _parseTimestamp(iso) {
  if (typeof iso !== 'string') return new Date(iso);
  const hasTz = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasTz ? iso : iso + 'Z');
}


/**
 * 0.44.66 - "x ago" for recent moments, the platform's date and time
 * past 30 days. Reads naive timestamps as UTC (as the server stores
 * them); the pages' own copies read them as local time, so a moment
 * that had just happened showed as "4h ago" in Yerevan.
 */
export function formatRelative(iso) {
  if (!iso) return '—';
  const then = _parseTimestamp(iso);
  if (isNaN(then.getTime())) return iso;
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return formatDateTime(iso);
}

/**
 * 0.44.66 - A clock time with seconds ("14:05:33", or "02:05:33 PM"),
 * in the viewer's time zone and the platform's time format. For "last
 * refreshed" lines. Takes a Date (default: now).
 */
export function formatClockWithSeconds(date = new Date()) {
  try {
    const p = new Intl.DateTimeFormat('en-GB', {
      timeZone: _currentUserTimezone, hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: _tf() === 'hh:mm a',
    }).formatToParts(date).reduce((o, x) => { o[x.type] = x.value; return o; }, {});
    const hm = `${p.hour}:${p.minute}:${p.second}`;
    return _tf() === 'hh:mm a' ? `${hm} ${(p.dayPeriod || '').toUpperCase()}`.trim() : hm;
  } catch {
    return date.toISOString().slice(11, 19);
  }
}

export function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const date = _parseTimestamp(iso);
    if (isNaN(date.getTime())) return iso;
    const parts = _extractParts(date, _currentUserTimezone, _tf());
    return `${_renderDate(parts, _df())} ${_renderTime(parts, _tf())}`;
  } catch {
    return iso;
  }
}


/**
 * 0.36.227 — Date-only variant. Useful for columns that show "Created"
 * (a row, not when on that row).
 */
export function formatDateOnly(iso) {
  if (!iso) return '—';
  try {
    const date = _parseTimestamp(iso);
    if (isNaN(date.getTime())) return iso;
    const parts = _extractParts(date, _currentUserTimezone, _tf());
    return _renderDate(parts, _df());
  } catch {
    return iso;
  }
}


/**
 * 0.36.227 — Time-only variant.
 */
export function formatTimeOnly(iso) {
  if (!iso) return '—';
  try {
    const date = _parseTimestamp(iso);
    if (isNaN(date.getTime())) return iso;
    const parts = _extractParts(date, _currentUserTimezone, _tf());
    return _renderTime(parts, _tf());
  } catch {
    return iso;
  }
}


/**
 * 0.36.547 — Calendar-date formatter for library `date` columns.
 * Input is 'YYYY-MM-DD' — a wall-clock date, NOT an instant. We
 * format it in the system date format WITHOUT any timezone
 * conversion, so admin sees the same day they typed regardless of
 * where in the world they open the page.
 *
 * Contrast with formatDateOnly() above, which takes an instant
 * (ISO timestamp) and shows it in the user's timezone — right for
 * created_at, wrong for a birthday column.
 */
export function formatCalendarDate(str) {
  if (!str) return '';
  const s = String(str).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;  // pass through anything not in YYYY-MM-DD
  return _renderDate({year: m[1], month: m[2], day: m[3]}, _df());
}


/**
 * 0.36.547 — Clock-time formatter for library `time` columns.
 * Input is 'HH:MM' or 'HH:MM:SS' — wall-clock, no timezone.
 * Formats per the system time_format (24-hour or 12-hour AM/PM).
 */
export function formatClockTime(str) {
  if (!str) return '';
  const s = String(str).trim();
  const m = s.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return s;  // pass through anything not in HH:MM
  return _renderTime(_wallTimeParts(m[1], m[2]), _tf());
}

/* 0.44.65 - A wall-clock hour and minute as _renderTime expects them:
 * with the 12-hour setting, the hour 01-12 plus AM / PM (the 24-hour
 * values were printed as they were, so 14:30 showed as "14:30"). */
function _wallTimeParts(h, m) {
  if (_tf() !== 'hh:mm a') return { hour: h, minute: m };
  const H = Number(h);
  return {
    hour: String(((H + 11) % 12) + 1).padStart(2, '0'),
    minute: m,
    dayPeriod: H < 12 ? 'AM' : 'PM',
  };
}

/**
 * 0.44.65 - Read a typed time back to 'HH:MM' (24-hour), or null when
 * it is not a time. Accepts what either platform format shows and the
 * usual shortcuts: '9:05', '09:05', '905', '9', '9:05 pm', '12 AM'.
 */
export function parseClockTime(str) {
  const s = String(str || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!s) return null;
  const m = s.match(/^(\d{1,2})(?:[:.]?(\d{2}))?\s*(am|pm|a|p)?$/);
  if (!m) return null;
  let h = Number(m[1]);
  const min = m[2] === undefined ? 0 : Number(m[2]);
  const period = m[3] ? m[3][0] : '';
  if (min > 59) return null;
  if (period) {
    if (h < 1 || h > 12) return null;
    if (period === 'a') h = h === 12 ? 0 : h;
    else h = h === 12 ? 12 : h + 12;
  } else if (h > 23) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}


/**
 * 0.36.547 — Calendar datetime formatter for library `datetime`
 * columns. Input is 'YYYY-MM-DDTHH:MM' (canonical stored form).
 * Treated as a wall-clock local time — no timezone conversion —
 * so the display matches what admin typed.
 */
export function formatCalendarDateTime(str) {
  if (!str) return '';
  const s = String(str).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return s;
  const parts = { year: m[1], month: m[2], day: m[3], ..._wallTimeParts(m[4], m[5]) };
  return `${_renderDate(parts, _df())} ${_renderTime(parts, _tf())}`;
}


// Internal: extract numeric date/time parts in the target timezone.
// We use Intl.DateTimeFormat formatToParts to get DST-correct values.
function _extractParts(date, timezone, timeFmt) {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    year:   'numeric',
    month:  '2-digit',
    day:    '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
    hour12: timeFmt === 'hh:mm a',
  });
  const out = {};
  for (const p of fmt.formatToParts(date)) {
    if (p.type !== 'literal') out[p.type] = p.value;
  }
  return out;
}


function _renderDate(parts, dateFmt) {
  switch (dateFmt) {
    case 'dd.mm.yyyy': return `${parts.day}.${parts.month}.${parts.year}`;
    case 'dd/mm/yyyy': return `${parts.day}/${parts.month}/${parts.year}`;
    case 'mm/dd/yyyy': return `${parts.month}/${parts.day}/${parts.year}`;
    case 'yyyy-mm-dd': return `${parts.year}-${parts.month}-${parts.day}`;
    // 0.44.69 - Month-name formats (names from Months and days).
    case 'dd MMM yyyy':   return `${parts.day} ${_monthName(parts.month, true, true)} ${parts.year}`;
    case 'dd MMMM yyyy':  return `${parts.day} ${_monthName(parts.month, false, true)} ${parts.year}`;
    case 'MMM dd, yyyy':  return `${_monthName(parts.month, true, true)} ${parts.day}, ${parts.year}`;
    case 'MMMM dd, yyyy': return `${_monthName(parts.month, false, true)} ${parts.day}, ${parts.year}`;
    default:           return `${parts.day}.${parts.month}.${parts.year}`;
  }
}


function _renderTime(parts, timeFmt) {
  if (timeFmt === 'hh:mm a') {
    // hour from Intl is '01'-'12' in hour12 mode; dayPeriod is 'AM'/'PM'
    const period = (parts.dayPeriod || '').toUpperCase();
    return `${parts.hour}:${parts.minute} ${period}`.trim();
  }
  // Default: 24h HH:mm
  return `${parts.hour}:${parts.minute}`;
}

/**
 * Clamp a value to an integer in [min, max]. Returns fallback if the
 * input can't be parsed as a finite number.
 * @param {*} v
 * @param {number} min
 * @param {number} max
 * @param {number} fallback
 * @returns {number}
 */
export function clampInt(v, min, max, fallback) {
  const n = Number.isFinite(v) ? Math.floor(v) : NaN;
  if (Number.isNaN(n)) return fallback;
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

/**
 * Convert a 2-letter ISO country code to a regional-indicator flag emoji.
 * Returns empty string for invalid input.
 * @param {string|null|undefined} code
 * @returns {string}
 */
export function codeToFlag(code) {
  if (!code || code.length !== 2) return '';
  return code.toUpperCase().replace(/./g, c =>
    String.fromCodePoint(127397 + c.charCodeAt(0))
  );
}

/**
 * Diacritic-insensitive lowercase normalization for substring search.
 * Strips combining characters so "Île-de-France" matches "ile".
 * Used by HenaketDropdown + HenaketMultiDropdown.
 * @param {*} s
 * @returns {string}
 */
export function normalize(s) {
  return (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * 0.36.175 - Returns an element's canonical identifier.
 *
 * Reads `element_id` first (new canonical field as of 0.36.175),
 * falling back to `name` (legacy field still present on all stored
 * structures for backwards compat with rules / translations /
 * submission payload keys).
 *
 * New code should use this helper. Old code reading el.name still
 * works because both fields are written in lockstep.
 *
 * @param {object} el  Element object from a form/page structure
 * @returns {string}   Identifier or empty string
 */
export function getElementId(el) {
  if (!el || typeof el !== 'object') return '';
  return el.element_id || el.name || '';
}

/**
 * 0.36.175 - Mirror of the backend's backfill_element_ids helper.
 *
 * Walks a form/page structure tree, adding `element_id` to any
 * element that has `name` but no `element_id`. Idempotent. Mutates
 * in place and returns the same object.
 *
 * Used defensively when receiving structures from the API (the backend
 * already backfills on response, but this lets the frontend handle
 * older cached or directly-edited data too).
 */
export function backfillElementIds(structure) {
  if (!Array.isArray(structure)) return structure;
  for (const el of structure) {
    if (!el || typeof el !== 'object') continue;
    if (!('element_id' in el) && 'name' in el) {
      el.element_id = el.name;
    }
    if (Array.isArray(el.children)) backfillElementIds(el.children);
    if (Array.isArray(el.columns)) {
      for (const col of el.columns) {
        if (col && Array.isArray(col.children)) {
          backfillElementIds(col.children);
        }
      }
    }
  }
  return structure;
}
